import React, { useState, useEffect, memo } from 'react';
import { Heart, Milestone, Compass } from 'lucide-react';
import { motion } from 'framer-motion';
import { MotionDiv, MotionComponent } from '@/utils/optimizedMotion';
import { fadeInVariant, staggerContainer } from '@/utils/optimizedMotion';
import ParallaxSection from '@/components/about/ParallaxSection';

/**
 * NewParadigmInfoSection Component
 * 
 * This component displays information about Meduler's new paradigm approach
 * to medication management with animated sections and icons.
 * 
 * Features:
 * - Parallax scrolling effects
 * - Staggered animations for better visual interest
 * - Responsive design for all device sizes
 * - Animated UI elements that respond to user interaction
 */
const NewParadigmInfoSection: React.FC = memo(() => {
  // Detect if on mobile for responsive animations
  const [isMobile, setIsMobile] = useState(false);
  // Check for reduced motion preference
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check for reduced motion preference
    const checkReducedMotion = () => {
      const query = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(query.matches);
    };
    
    checkMobile();
    checkReducedMotion();
    
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  // Define animation variants for paradigm features
  const featureVariants = {
    hidden: { opacity: 0, x: 20 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        delay: i * 0.15,
        ease: "easeOut"
      }
    })
  };

  // Define hover animation for icon containers
  const iconContainerVariants = {
    initial: { backgroundColor: "#F3E8FF" }, // light purple
    hover: isMobile ? {} : { backgroundColor: "#E9D5FF", scale: 1.05 }, // medium purple with scale
  };
  
  return (
    <ParallaxSection variant="bottom" className="max-w-6xl mx-auto" reduceMotion={prefersReducedMotion}>
      <motion.div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, margin: "-5%" }}
      >
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center p-6 md:p-10">
          <MotionDiv 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.span 
              className="inline-block px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-sm font-medium mb-3"
              variants={fadeInVariant}
            >
              A NEW PARADIGM
            </motion.span>
            
            <motion.h2 
              className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white"
              variants={fadeInVariant}
            >
              Beyond Traditional Medication Support
            </motion.h2>
            
            <motion.div 
              className="space-y-6"
              variants={fadeInVariant}
            >
              <motion.p 
                className="text-gray-600 dark:text-gray-300"
                variants={fadeInVariant}
              >
                The Meduler Integration System represents not just a new product but a new paradigm in medication support technology. By shifting from tracking adherence to supporting integration, we create space for individuals to discover their own sustainable relationship with medication.
              </motion.p>
              
              <motion.p 
                className="text-gray-600 dark:text-gray-300"
                variants={fadeInVariant}
              >
                This approach recognizes that effective medication integration is not a matter of compliance with external directives but emerges from the harmonious alignment of personal rhythms, physical environments, psychological relationships, and social contexts.
              </motion.p>
            </motion.div>
          </MotionDiv>

          <motion.div 
            className="bg-purple-50 dark:bg-gray-700 rounded-2xl p-6 md:p-8 shadow-sm border border-purple-100 dark:border-gray-600"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true, margin: "-10%" }}
          >
            <motion.div 
              className="space-y-8"
              initial="hidden"
              whileInView="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: isMobile ? 0.2 : 0.3
                  }
                }
              }}
              viewport={{ once: true }}
            >
              {[
                {
                  icon: Heart,
                  title: "From Compliance to Integration",
                  description: "Medication-taking is not isolated events to be tracked, but elements to be woven into the complex tapestry of lived experience."
                },
                {
                  icon: Milestone,
                  title: "From Monitoring to Partnership",
                  description: "Our technology is not an oversight mechanism, but a collaborative explorer that helps discover sustainable patterns."
                },
                {
                  icon: Compass,
                  title: "From Universality to Individuality",
                  description: "Effective medication integration looks radically different across diverse lives, requiring personalized rather than standardized approaches."
                }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div 
                    key={index}
                    className="flex items-start gap-4"
                    custom={index}
                    variants={featureVariants}
                  >
                    <motion.div 
                      className="bg-purple-100 dark:bg-purple-900 rounded-full p-2 mt-0.5" 
                      aria-hidden="true"
                      variants={iconContainerVariants}
                      initial="initial"
                      whileHover="hover"
                      whileInView={{ 
                        backgroundColor: ["#F3E8FF", "#E9D5FF", "#F3E8FF"],
                        transition: { duration: 2, ease: "easeInOut", repeat: 0, delay: index * 0.2 }
                      }}
                      viewport={{ once: true }}
                    >
                      <Icon className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                    </motion.div>
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-gray-900 dark:text-white">{item.title}</h3>
                      <p className="text-gray-600 dark:text-gray-300">{item.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </ParallaxSection>
  );
});

NewParadigmInfoSection.displayName = 'NewParadigmInfoSection';

export default NewParadigmInfoSection;
